import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import { RgCraPayoutRequestMutation } from './__generated__/PlayerReportComplaintsRequestForm';

const query = graphql`
  query SanityPlayerReportComplaintsRequestForm {
    sanityPlayerReportComplaintsRequestForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const rgCraPayoutRequestMutation = gql`
  mutation RgCraPayoutRequest($playerId: ID!) {
    rgCraPayoutRequest(playerId: $playerId)
  }
`;

const PlayerReportComplaintsRequestForm: FC<{
  playerId: PlayerId;
}> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerReportComplaintsRequestFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerReportComplaintsRequestForm;

  const [reportBonusRequestState, reportBonusRequest] =
    useMutation<RgCraPayoutRequestMutation>(rgCraPayoutRequestMutation);

  const { close } = useModalContext();
  const { t } = useTranslate();

  const onSubmit = () => {
    reportBonusRequest({
      playerId,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error?.message);
        return;
      }
      if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form defaultValues={{}} onSubmit={onSubmit} className="grid gap-6">
          <div className="text-gray-400 dark:text-white">
            {t(form.description)}
          </div>
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={reportBonusRequestState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerReportComplaintsRequestForm;
